import React, { useState } from 'react';

/**
 * ISO27001Section6 component displaying documentation requirements for Section 6
 * @returns {JSX.Element}
 */
function ISO27001Section6() {
  // State to track expanded sections
  const [expandedSections, setExpandedSections] = useState({
    '6.1.1': true,
    '6.1.2': true,
    '6.1.3': true,
    '6.2': true
  });

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Section 6 requirements data
  const requirementsData = [
    {
      section: '6.1.1 General actions to address risks and opportunities',
      requirements: [
        {
          requirement: 'Consider organizational context and requirements to determine risks and opportunities',
          documents: [
            '04-ISMS Risk Assessment and Risk Treatment Process',
            'Risk Register'
          ],
          responsibility: 'Client'
        },
        {
          requirement: 'Plan actions to address risks and opportunities',
          documents: [
            'Risk Treatment Plan',
            'Risk Action Tracking Log'
          ],
          responsibility: 'Client'
        },
        {
          requirement: 'Technical risk identification and monitoring',
          documents: [
            'Technical Risk Assessment Results',
            'Vulnerability Management Program'
          ],
          responsibility: 'Kolega'
        }
      ]
    },
    {
      section: '6.1.2 Information security risk assessment',
      requirements: [
        {
          requirement: 'Define and apply risk assessment process',
          documents: [
            '04-ISMS Risk Assessment and Risk Treatment Process',
            'Risk Management Policy'
          ],
          responsibility: 'Client'
        },
        {
          requirement: 'Conduct risk assessments',
          documents: [
            'Risk Assessment Results',
            'Risk Register'
          ],
          responsibility: 'Client'
        },
        {
          requirement: 'Technical risk assessment',
          documents: [
            'Technical Vulnerability Assessment Reports',
            'Penetration Test Reports',
            'Technical Risk Register'
          ],
          responsibility: 'Kolega'
        }
      ]
    },
    {
      section: '6.1.3 Information security risk treatment',
      requirements: [
        {
          requirement: 'Define and apply risk treatment process',
          documents: [
            '04-ISMS Risk Assessment and Risk Treatment Process'
          ],
          responsibility: 'Client'
        },
        {
          requirement: 'Document controls and produce Statement of Applicability',
          documents: [
            '11-ISMS Statement of Applicability (SoA)'
          ],
          responsibility: 'Client'
        },
        {
          requirement: 'Formulate risk treatment plan',
          documents: [
            'Risk Treatment Plan',
            'Risk Owner Approval Records'
          ],
          responsibility: 'Client'
        },
        {
          requirement: 'Technical risk remediation',
          documents: [
            'Technical Control Implementation Plan',
            'Remediation Status Reports'
          ],
          responsibility: 'Kolega'
        }
      ]
    },
    {
      section: '6.2 Information security objectives and planning to achieve them',
      requirements: [
        {
          requirement: 'Establish information security objectives',
          documents: [
            '10-ISMS Information Security Objectives Plan'
          ],
          responsibility: 'Client'
        },
        {
          requirement: 'Plan to achieve objectives',
          documents: [
            'Security Implementation Roadmap',
            'Resource Allocation Plan'
          ],
          responsibility: 'Client'
        },
        {
          requirement: 'Technical security objectives',
          documents: [
            'Technical Security Roadmap',
            'Security Control Implementation Plan'
          ],
          responsibility: 'Kolega'
        }
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">ISO 27001 Section 6: Planning</h1>
      
      <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100 mb-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Documentation Requirements Matrix</h2>
        <p className="text-gray-700 mb-6">
          This matrix outlines the ISO 27001 Section 6 requirements and the specific documents that need 
          to be created to demonstrate compliance. It includes both client responsibilities and Kolega 
          responsibilities, clearly indicating which party is responsible for creating and maintaining each document.
        </p>
        
        <div className="bg-blue-50 rounded-lg p-6 mb-8 border-l-4 border-blue-500">
          <h3 className="text-xl font-bold mb-3 text-gray-800">Overview of Section 6</h3>
          <p className="text-gray-700">
            Section 6 "Planning" focuses on risk assessment, risk treatment, and establishing information 
            security objectives. This section requires organizations to identify risks and opportunities, 
            assess information security risks, develop risk treatment plans, and establish measurable 
            information security objectives.
          </p>
        </div>
        
        <div className="overflow-hidden mb-8">
          <table className="min-w-full divide-y divide-gray-200 border">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ISO 27001 Section</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subsection</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requirement</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required Document(s)</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Responsibility</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requirementsData.flatMap((section, sectionIndex) => 
                section.requirements.map((req, reqIndex) => (
                  <tr key={`${sectionIndex}-${reqIndex}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {reqIndex === 0 ? '6. Planning' : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {reqIndex === 0 ? section.section : ''}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {req.requirement}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="space-y-2">
                        {req.documents.map((doc, docIndex) => (
                          <div key={docIndex} className="bg-gray-50 rounded-md p-2 border border-gray-100 hover:border-blue-200 transition-colors">
                            <div className="flex items-start">
                              <span className="text-blue-500 mr-2 mt-0.5 flex-shrink-0">•</span>
                              <span className="flex-grow font-medium">{doc}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        req.responsibility === 'Kolega' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {req.responsibility}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <div className="space-y-6">
          {requirementsData.map((section, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div 
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleSection(section.section.split(' ')[0])}
              >
                <h3 className="text-xl font-bold text-gray-800">{section.section}</h3>
                <span>{expandedSections[section.section.split(' ')[0]] ? '▼' : '▶'}</span>
              </div>
              
              {expandedSections[section.section.split(' ')[0]] && (
                <div className="mt-4 space-y-4">
                  {section.requirements.map((req, reqIndex) => (
                    <div key={reqIndex} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center mb-2">
                        <div className={`w-3 h-3 ${req.responsibility === 'Kolega' ? 'bg-blue-500' : 'bg-amber-500'} rounded-full mr-2`}></div>
                        <h5 className={`font-bold ${req.responsibility === 'Kolega' ? 'text-blue-700' : 'text-amber-700'}`}>
                          {req.responsibility} Responsibility
                        </h5>
                      </div>
                      
                      <p className="text-gray-700 mb-3 font-medium">{req.requirement}</p>
                      
                      <div className="ml-5">
                        <h6 className="font-semibold text-gray-700 mb-2">Required Documents:</h6>
                        <div className="space-y-2">
                          {req.documents.map((doc, docIndex) => (
                            <div key={docIndex} className="bg-white rounded-md p-3 border border-gray-200 hover:border-blue-200 transition-colors">
                              <p className="font-semibold text-gray-800">{doc}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold mb-2">Responsibility Legend:</h3>
          <div className="flex items-center space-x-6">
            <div className="flex items-center">
              <span className="w-4 h-4 inline-block rounded-full bg-blue-100 mr-2"></span>
              <span className="text-sm">Kolega Responsibility</span>
            </div>
            <div className="flex items-center">
              <span className="w-4 h-4 inline-block rounded-full bg-amber-100 mr-2"></span>
              <span className="text-sm">Client Responsibility</span>
            </div>
          </div>
        </div>
        
        <div className="mt-8 bg-green-50 rounded-xl p-6 border-l-4 border-green-500">
          <h3 className="text-xl font-bold mb-3 text-gray-800">Implementation Guidance</h3>
          <p className="text-gray-700 mb-4">
            For effective implementation of Section 6 requirements, we recommend the following approach:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-blue-700 mb-2">Kolega's Implementation Steps:</h4>
              <ol className="list-decimal pl-6 text-gray-700 space-y-2">
                <li>Develop and implement a Vulnerability Management Program</li>
                <li>Conduct technical vulnerability assessments and penetration tests</li>
                <li>Create and maintain a Technical Risk Register</li>
                <li>Develop Technical Control Implementation Plans</li>
                <li>Provide regular Remediation Status Reports</li>
                <li>Establish Technical Security Roadmap with measurable objectives</li>
              </ol>
            </div>
            
            <div>
              <h4 className="font-bold text-amber-700 mb-2">Client's Implementation Steps:</h4>
              <ol className="list-decimal pl-6 text-gray-700 space-y-2">
                <li>Develop the 04-ISMS Risk Assessment and Risk Treatment Process</li>
                <li>Create and maintain a comprehensive Risk Register</li>
                <li>Develop Risk Treatment Plans with clear ownership</li>
                <li>Establish a Risk Action Tracking Log</li>
                <li>Create the Statement of Applicability (SoA)</li>
                <li>Develop the Information Security Objectives Plan</li>
                <li>Create a Security Implementation Roadmap with resource allocation</li>
              </ol>
            </div>
          </div>
          
          <div className="mt-4 bg-white rounded-lg p-4 border border-gray-200">
            <h4 className="font-bold text-gray-800 mb-2">Integration Points:</h4>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Client Risk Registers should incorporate technical risks identified by Kolega</li>
              <li>Client Risk Treatment Plans should reference Kolega's Technical Control Implementation Plans</li>
              <li>Client Security Implementation Roadmaps should align with Kolega's Technical Security Roadmap</li>
              <li>Regular joint reviews should be conducted to ensure alignment between technical and organizational security objectives</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ISO27001Section6;
